import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  TextField,
} from '@mui/material';

import { PLACES_AUTOCOMPLETE_LIMITS } from '@bandido/shared/types';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { connectorCapabilitiesAreActionable } from '@frontend/features/rutero/connectorCapabilitiesQueryKey';
import { useAutocompleteFocus } from '../useAutocompleteFocus';
import {
  apiErrorMessage,
  ConnectorInfo,
  usePlacesAutocomplete,
  useConnectorCapabilities,
} from '../queries';
import { deriveServedByForVertical } from '../connectorCapabilityResolution';
import {
  PlaceOption,
  PlaceRef,
  flattenPlaceOptions,
  placesNoticeReason,
  toPlaceRef,
} from '../places';
import { PlaceAutocompletePopper } from './PlaceAutocomplete';
import { PlaceOptionRow } from './PlaceOptionRow';

interface Props {
  label: string;
  value: PlaceRef[];
  onChange: (places: PlaceRef[]) => void;
  width?: number | string;
}

/**
 * Ciudades del multibuscador de circuitos contra el maestro de lugares
 * (ubikeishon, `plc_*`).
 *
 * NO reusa `PlaceAutocomplete`: el filtro de circuitos es multi-select y AND
 * (el circuito tiene que pasar por TODAS las ciudades elegidas) mientras que
 * `PlaceAutocomplete` es single-select — el resto del epic no tiene este
 * caso. Comparte con `PlaceAutocomplete` la MISMA autoridad de conectores
 * (`servedBy` derivado de `GET /rutero/connectors`, nunca hardcodeado, fail-
 * closed sin conector configurado) y la misma lógica pura de `places.ts`;
 * solo cambia la forma del Autocomplete (`multiple`) y, por eso, cómo se
 * reconstruye el `value` controlado — ver el comentario de `selected` abajo.
 *
 * `kinds: ['city']` fijo (a diferencia de `PlaceAutocomplete`, que lo recibe
 * por prop): es la única forma de destino que tiene sentido para este filtro,
 * así que no hace falta que cada caller lo repita.
 */
export function CircuitDestinationsPlaceInput({
  label,
  value,
  onChange,
  width = 320,
}: Props) {
  const { t } = useTranslation();
  const [debouncedQuery, setQuery] = useDebouncedState('', 250);
  // Multi-select: lo elegido son chips, así que el input no muestra ninguna
  // etiqueta y `selectedLabel` es siempre `''`. El resto del comportamiento
  // de foco/blur es el mismo que el de los single (#2226).
  const focus = useAutocompleteFocus({ selectedLabel: '', onQueryChange: setQuery });

  const {
    data: connectors,
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
  } = useConnectorCapabilities();
  const capabilitiesActionable = connectorCapabilitiesAreActionable({
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
  });
  const servedBy = useMemo(
    () =>
      deriveServedByForVertical(
        connectors as ConnectorInfo[] | undefined,
        'circuits',
      ),
    [connectors],
  );
  const pickerReady = capabilitiesActionable && servedBy.length > 0;

  const {
    data,
    isFetching: searching,
    isError: searchFailed,
    error: searchError,
  } = usePlacesAutocomplete(debouncedQuery, {
    kinds: ['city'],
    servedBy,
    enabled: pickerReady,
  });

  const options = useMemo(
    () => flattenPlaceOptions(data?.results ?? []),
    [data?.results],
  );
  const selectedIds = useMemo(() => new Set(value.map((place) => place.id)), [value]);
  /** Lo ya elegido no se vuelve a ofrecer (mismo criterio que el input nativo). */
  const available = options.filter((option) => !selectedIds.has(option.id));

  // Mismo problema que `selected` en `PlaceAutocomplete`, aplicado a una
  // lista: `useAutocomplete` compara el `value` controlado con el del render
  // anterior POR REFERENCIA, y este componente re-renderiza en cada tecla
  // (`inputValue`/`debouncedQuery`). Reconstruir el array inline en cada
  // render (sin memo) le da una referencia nueva aunque el contenido no haya
  // cambiado, y el input se resetea sin dejar seguir escribiendo. La firma
  // (id+nombre+kind+code de cada elemento, en orden) es la dependencia real;
  // `value` en sí NO puede ir en el arreglo de deps porque su referencia SÍ
  // cambia en cada selección/deselección, que es exactamente cuándo tiene
  // que recalcularse.
  const valueSignature = value
    .map((place) => `${place.id}:${place.kind}:${place.name}:${place.code ?? ''}`)
    .join('|');
  const selected = useMemo<PlaceOption[]>(
    () =>
      value.map((place) => ({
        id: place.id,
        kind: place.kind,
        name: place.name,
        ...(place.code ? { code: place.code } : {}),
      })),
    [valueSignature],
  );

  const noticeReason = placesNoticeReason({
    ready: pickerReady,
    queryLength: debouncedQuery.trim().length,
    minLength: PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
    isError: searchFailed,
  });
  const showLoading =
    focus.editing &&
    searching &&
    debouncedQuery.trim().length >= PLACES_AUTOCOMPLETE_LIMITS.queryMinLength;

  return (
    <Autocomplete<PlaceOption, true, false, false>
      multiple
      size="small"
      sx={{ width }}
      options={available}
      loading={showLoading}
      disabled={!pickerReady}
      value={selected}
      onChange={(_event, places, reason) => {
        if (focus.commitValueChange(reason)) onChange(places.map(toPlaceRef));
      }}
      inputValue={focus.inputValue}
      onFocus={focus.onFocus}
      onBlur={focus.onBlur}
      onInputChange={focus.onInputChange}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, sel) => option.id === sel.id}
      // El ranking lo hace ubikeishon: no re-filtrar por el label.
      filterOptions={(opts) => opts}
      PopperComponent={PlaceAutocompletePopper}
      ListboxProps={{
        sx: {
          py: 0,
          '& .MuiAutocomplete-option': {
            minHeight: 64,
            alignItems: 'stretch',
            px: 2,
            py: 1,
          },
        },
      }}
      renderTags={(places, getTagProps) =>
        places.map((place, index) => (
          <Chip
            size="small"
            label={place.name}
            {...getTagProps({ index })}
            key={place.id}
          />
        ))
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <PlaceOptionRow option={option} />
        </li>
      )}
      noOptionsText={
        noticeReason === 'disabled'
          ? t('travelServices.places.noProviders')
          : noticeReason === 'error'
            ? (apiErrorMessage(searchError) ?? t('travelServices.places.searchError'))
            : noticeReason === 'tooShort'
              ? t('travelServices.places.typeToSearch', {
                count: PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
              })
              : t('travelServices.places.noResults')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('travelServices.circuits.destinationsPlaceholder')}
          error={noticeReason === 'error'}
          helperText={
            noticeReason === 'disabled'
              ? t('travelServices.places.noProviders')
              : noticeReason === 'error'
                ? (apiErrorMessage(searchError) ?? t('travelServices.places.searchError'))
                : undefined
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {showLoading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
